import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useWS } from "@/contexts/WebSocketContext";
import { Bell, Warning, CheckCircle, Info } from "@phosphor-icons/react";
import { toast } from "sonner";
import PageShell from "@/components/layout/PageShell";
import PageHeader from "@/components/layout/PageHeader";

const sevIcon = { warning: Warning, danger: Warning, info: Info, success: CheckCircle };
const sevColor = { warning: "text-[#F59E0B] border-[#F59E0B]/30", danger: "text-[#FF3B30] border-[#FF3B30]/30", info: "text-[#007AFF] border-[#007AFF]/30", success: "text-[#10B981] border-[#10B981]/30" };

export default function Notifications() {
  const { lastNotif } = useWS();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("inbox");
  const [prefs, setPrefs] = useState({ dashboard: true, email: false, telegram: false, whatsapp: false, drawdown_threshold: 10, margin_threshold: 100, email_address: "", telegram_chat_id: "", whatsapp_number: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data);
    } catch {
      toast.error("Failed to load notifications");
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (lastNotif) load(); }, [lastNotif]);
  useEffect(() => { (async () => { const { data } = await api.get("/notifications/preferences"); setPrefs(p => ({ ...p, ...data })); })(); }, []);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      setItems(items.map(i => i.id === id ? { ...i, read: true } : i));
    } catch {
      toast.error("Failed to mark notification as read");
    }
  };

  const savePrefs = async () => {
    try { await api.put("/notifications/preferences", prefs); toast.success("Preferences saved"); } catch { toast.error("Failed"); }
  };

  const unreadCount = items.filter((i) => !i.read).length;

  return (
    <PageShell>
      <PageHeader eyebrow="Alert Stream" title="NOTIFICATIONS">
        {unreadCount > 0 && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 font-mono-d text-[10px] uppercase tracking-widest border border-app-accent/35 text-app-accent bg-app-accent-soft">
            <Bell size={12} weight="fill" />
            {unreadCount} unread
          </span>
        )}
      </PageHeader>

      <div className="page-filter-bar mb-6">
        <div className="page-filter-field w-full sm:w-52">
          <span className="page-filter-label">View</span>
          <div className="flex gap-1.5">
            {["inbox", "preferences"].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                data-testid={`tab-${t}`}
                className={`flex-1 min-h-[40px] px-3 text-xs font-mono-d uppercase tracking-widest border transition-colors ${
                  tab === t ? "border-app-accent text-app-accent bg-app-accent-soft" : "border-app-border text-app-subtle hover:bg-app-hover"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
        {tab === "inbox" && (
          <>
            <div className="page-filter-field flex-1 min-w-0">
              <span className="page-filter-label">Inbox</span>
              <div className="flex items-center min-h-[40px] px-3 border border-app-border bg-app-bg font-mono-d text-sm text-app-text">
                {items.length} notification{items.length === 1 ? "" : "s"}
              </div>
            </div>
            <div className="page-filter-field w-full sm:w-36 lg:w-40">
              <span className="page-filter-label">Unread</span>
              <div className={`flex items-center min-h-[40px] px-3 border font-mono-d text-sm tabular-nums ${
                unreadCount > 0 ? "border-app-accent/40 text-app-accent bg-app-accent-soft" : "border-app-border bg-app-bg text-app-text"
              }`}>
                {unreadCount}
              </div>
            </div>
          </>
        )}
      </div>

      {tab === "inbox" && (
        <div className="border border-app-border bg-app-surface" data-testid="notif-list">
          {items.length === 0 && <div className="p-8 text-center font-mono-d text-xs text-app-muted">NO NOTIFICATIONS</div>}
          {items.map(n => {
            const Icon = sevIcon[n.severity] || Bell;
            return (
              <div key={n.id} data-testid={`notif-${n.id}`} onClick={() => !n.read && markRead(n.id)}
                className={`flex items-start gap-3 px-4 py-3 border-b border-app-border/70 cursor-pointer hover:bg-app-hover ${!n.read ? "bg-[#0f1a2a]/30" : ""}`}>
                <Icon size={16} weight="fill" className={sevColor[n.severity]?.split(" ")[0]} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm break-words">{n.message}</div>
                  <div className="font-mono-d text-[10px] text-app-muted mt-0.5 uppercase tracking-widest">
                    {n.type} · {new Date(n.timestamp).toLocaleString()}
                  </div>
                </div>
                {!n.read && <div className="w-1.5 h-1.5 rounded-full bg-[#007AFF] mt-2" />}
              </div>
            );
          })}
        </div>
      )}

      {tab === "preferences" && (
        <div className="border border-app-border bg-app-surface p-4 sm:p-6 w-full max-w-2xl" data-testid="notif-prefs">
          <div className="space-y-5">
            {[
              ["dashboard", "Dashboard (in-app)", null, null],
              ["email", "Email", "email_address", "you@email.com"],
              ["telegram", "Telegram", "telegram_chat_id", "Chat ID"],
              ["whatsapp", "WhatsApp", "whatsapp_number", "+1234567890"],
            ].map(([key, label, field, ph]) => (
              <div key={key} className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <label className="flex items-center gap-2 sm:w-40 shrink-0 min-h-[44px]">
                  <input type="checkbox" checked={prefs[key]} onChange={(e) => setPrefs({ ...prefs, [key]: e.target.checked })} data-testid={`toggle-${key}`} className="accent-[#007AFF] w-5 h-5" />
                  <span className="text-sm">{label}</span>
                </label>
                {field && (
                  <input value={prefs[field] || ""} onChange={(e) => setPrefs({ ...prefs, [field]: e.target.value })} placeholder={ph}
                    data-testid={`input-${field}`} className="flex-1 w-full bg-app-bg border border-app-border px-3 py-2.5 text-sm min-h-[44px]" />
                )}
              </div>
            ))}
            <div className="pt-4 border-t border-app-border">
              <div className="font-mono-d text-[10px] uppercase tracking-widest text-app-subtle mb-3">Thresholds</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-app-subtle">Drawdown alert &gt; (%)</label>
                  <input type="number" value={prefs.drawdown_threshold} onChange={(e) => setPrefs({ ...prefs, drawdown_threshold: parseFloat(e.target.value) })} data-testid="dd-thresh"
                    className="w-full mt-1 bg-app-bg border border-app-border px-3 py-2 font-mono-d text-sm" />
                </div>
                <div>
                  <label className="text-xs text-app-subtle">Margin level &lt; (%)</label>
                  <input type="number" value={prefs.margin_threshold} onChange={(e) => setPrefs({ ...prefs, margin_threshold: parseFloat(e.target.value) })} data-testid="margin-thresh"
                    className="w-full mt-1 bg-app-bg border border-app-border px-3 py-2 font-mono-d text-sm" />
                </div>
              </div>
            </div>
            <button onClick={savePrefs} data-testid="save-prefs" className="btn-touch w-full sm:w-auto px-4 py-2.5 bg-[#007AFF] hover:bg-[#3395FF] text-sm">Save Preferences</button>
          </div>
        </div>
      )}
    </PageShell>
  );
}
